/**
 * Report export helpers (Excel + PDF). The heavy libraries are loaded on demand,
 * so they only download when someone actually exports.
 *
 * Money columns use "INR" in headers instead of the ₹ sign: the standard PDF fonts
 * have no ₹ glyph, and keeping both formats identical avoids surprises.
 */
export interface ExportColumn<T> {
  header: string;
  value: (row: T) => string | number;
  /** Right-aligned, formatted with 2 decimals. */
  numeric?: boolean;
  /** Excel column width in characters. */
  width?: number;
}

export interface ExportOptions<T> {
  title: string;
  fileName: string;
  subtitle?: string;
  columns: ExportColumn<T>[];
  rows: T[];
  summary?: { label: string; value: string }[];
}

/** "name-2026-10-17" style file name (extension is added on export). */
export const reportFileName = (name: string) => `${name}-${new Date().toISOString().slice(0, 10)}`;

const BRAND_RGB: [number, number, number] = [17, 95, 60];

function download(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const generatedAt = () =>
  new Date().toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

export async function exportToExcel<T>({ title, fileName, subtitle, columns, rows, summary = [] }: ExportOptions<T>) {
  const mod = await import("exceljs");
  const ExcelJS = (mod as { default?: typeof import("exceljs") }).default ?? mod;

  const workbook = new ExcelJS.Workbook();
  workbook.created = new Date();
  const sheet = workbook.addWorksheet(title.replace(/[\\/?*[\]:]/g, " ").slice(0, 31));
  const lastCol = Math.max(columns.length, 1);

  const titleRow = sheet.addRow([title]);
  titleRow.font = { bold: true, size: 14 };
  sheet.mergeCells(titleRow.number, 1, titleRow.number, lastCol);

  const metaRow = sheet.addRow([[subtitle, `Generated ${generatedAt()}`].filter(Boolean).join("  •  ")]);
  metaRow.font = { size: 9, color: { argb: "FF666666" } };
  sheet.mergeCells(metaRow.number, 1, metaRow.number, lastCol);

  summary.forEach(({ label, value }) => {
    const row = sheet.addRow([label, value]);
    row.getCell(1).font = { bold: true };
  });
  sheet.addRow([]);

  const headerRow = sheet.addRow(columns.map((c) => c.header));
  headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
  headerRow.alignment = { vertical: "middle" };
  headerRow.eachCell((cell, colNumber) => {
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF115F3C" } };
    if (columns[colNumber - 1]?.numeric) cell.alignment = { horizontal: "right", vertical: "middle" };
  });

  rows.forEach((row) => {
    const added = sheet.addRow(columns.map((c) => c.value(row)));
    columns.forEach((c, index) => {
      if (c.numeric) {
        const cell = added.getCell(index + 1);
        cell.numFmt = "#,##0.00";
        cell.alignment = { horizontal: "right" };
      }
    });
  });

  columns.forEach((c, index) => {
    const longest = rows.slice(0, 200).reduce((max, row) => Math.max(max, String(c.value(row)).length), c.header.length);
    sheet.getColumn(index + 1).width = c.width ?? Math.min(Math.max(longest + 2, 10), 40);
  });
  sheet.views = [{ state: "frozen", ySplit: headerRow.number }];
  sheet.autoFilter = {
    from: { row: headerRow.number, column: 1 },
    to: { row: headerRow.number, column: lastCol },
  };

  const buffer = await workbook.xlsx.writeBuffer();
  download(
    new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    `${fileName}.xlsx`,
  );
}

export async function exportToPdf<T>({ title, fileName, subtitle, columns, rows, summary = [] }: ExportOptions<T>) {
  const [{ jsPDF }, { default: autoTable }] = await Promise.all([import("jspdf"), import("jspdf-autotable")]);

  const doc = new jsPDF({ orientation: columns.length > 6 ? "landscape" : "portrait", unit: "pt", format: "a4" });
  const margin = 36;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text(title, margin, 42);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(110);
  doc.text([subtitle, `Generated ${generatedAt()}`].filter(Boolean).join("   |   "), margin, 58);

  let startY = 72;
  if (summary.length > 0) {
    doc.setTextColor(40);
    doc.setFontSize(10);
    summary.forEach(({ label, value }, index) => {
      doc.setFont("helvetica", "bold");
      doc.text(`${label}:`, margin, startY + index * 14);
      doc.setFont("helvetica", "normal");
      doc.text(value, margin + 110, startY + index * 14);
    });
    startY += summary.length * 14 + 6;
  }

  autoTable(doc, {
    startY,
    margin: { left: margin, right: margin },
    head: [columns.map((c) => c.header)],
    body: rows.map((row) =>
      columns.map((c) => {
        const v = c.value(row);
        return c.numeric && typeof v === "number"
          ? v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : String(v);
      }),
    ),
    styles: { fontSize: 8, cellPadding: 4, overflow: "linebreak" },
    headStyles: { fillColor: BRAND_RGB, textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [244, 247, 245] },
    columnStyles: Object.fromEntries(
      columns.map((c, index) => [index, c.numeric ? { halign: "right" as const } : {}]),
    ),
  });

  const pages = doc.getNumberOfPages();
  for (let page = 1; page <= pages; page += 1) {
    doc.setPage(page);
    doc.setFontSize(8);
    doc.setTextColor(130);
    doc.text(
      `Page ${page} of ${pages}`,
      doc.internal.pageSize.getWidth() - margin,
      doc.internal.pageSize.getHeight() - 20,
      { align: "right" },
    );
  }

  doc.save(`${fileName}.pdf`);
}
